import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Button, Stack, Typography } from "@mui/material";
import {
  NotificationAction,
  NotificationItem,
} from "../../models/Notification";
import { useAuth } from "../../contexts/UserContext";
import NotificationService from "../../service/notification.service";
import CustomModal from "../modal/CustomModal";

// --- Constants & Helpers ---
const ACTION_ORDER_MAP: Record<string, number> = {
  danger: 1, // Left
  primary: 2, // Right
};

const sortActions = (actions: NotificationAction[]) => {
  return [...actions].sort((a, b) => {
    const orderA = ACTION_ORDER_MAP[a.style] || 99;
    const orderB = ACTION_ORDER_MAP[b.style] || 99;
    return orderA - orderB;
  });
};

const getButtonProps = (style: string) => {
  switch (style) {
    case "danger":
      return { color: "error" as const, variant: "outlined" as const };
    case "secondary":
      return { color: "secondary" as const, variant: "text" as const };
    default: // primary
      return { color: "primary" as const, variant: "contained" as const };
  }
};

export default function GlobalNotification() {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, user, organization } = useAuth();
  const [searchParams] = useSearchParams();

  // --- State ---
  const [notificationQueue, setNotificationQueue] = useState<
    NotificationItem[]
  >([]);
  const [openAlert, setOpenAlert] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentLang = i18n.language === "th" ? "th" : "en";
  const currentNotification = notificationQueue[0] || null;

  const userId = user?.refId || user?.refId || "unknown_user";
  const orgId = organization?.id || organization?.id || "unknown_org";
  const storageKeyDate = `noti_check_${userId}_${orgId}`;

  // --- Effect 1: Fetch Notifications ---
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!user || !isAuthenticated()) return;

      const todayDateStr = new Date().toDateString();
      const lastCheckDateStr = localStorage.getItem(storageKeyDate);
      const isForceTest = searchParams.get("force_noti") === "true";

      if (lastCheckDateStr !== todayDateStr || isForceTest) {
        try {
          const notifications =
            await NotificationService.getPendingNotifications();

          if (notifications?.length > 0) {
            const sorted = notifications.sort(
              (a, b) => b.behavior.priority - a.behavior.priority,
            );
            setNotificationQueue(sorted);
            // ❌ เอาการเซฟ localStorage ออกจากตรงนี้
            // ปล่อยให้มันไม่ถูกเซฟ เพื่อที่ถ้ารีเฟรช มันจะเช็ค API ใหม่อีกรอบ
          } else {
            // ✅ เซฟ localStorage เฉพาะกรณีที่ "ไม่มี" Noti ค้างอยู่แล้วเท่านั้น
            if (!isForceTest) {
              localStorage.setItem(storageKeyDate, todayDateStr);
            }
          }
        } catch (error) {
          console.error("Failed to check notification", error);
        }
      }
    };

    fetchNotifications();
  }, [user, isAuthenticated, searchParams, storageKeyDate]);

  // --- Effect 2: Process Queue ---
  useEffect(() => {
    if (notificationQueue.length > 0 && !openAlert) {
      setOpenAlert(true);
    }
  }, [notificationQueue.length, openAlert]);

  // --- Handlers ---
  const handleCloseNotification = () => {
    setOpenAlert(false);
    setTimeout(() => {
      setNotificationQueue((prev) => prev.slice(1));
    }, 300);
  };

  const handleActionClick = (actionKey: string) => {
    if (isSubmitting || !currentNotification) return;

    setIsSubmitting(true);
    handleCloseNotification();

    NotificationService.respondToNotification(
      currentNotification._id,
      actionKey,
    )
      .catch((error) => {
        console.error("Action failed", error);
      })
      .finally(() => {
        // การกดปิด/ตอบ advance คิวไปแล้วเสมอ (handleCloseNotification) — ถ้าเป็น
        // อันสุดท้าย เซฟ localStorage ใน finally เพื่อไม่ให้เด้งซ้ำวันนี้ แม้ API จะ error
        // (มิฉะนั้น noti แบบ info ที่ dismiss ฝั่ง server ไม่ได้จะเด้งใหม่ทุกครั้งที่ refresh)
        if (notificationQueue.length <= 1) {
          localStorage.setItem(storageKeyDate, new Date().toDateString());
        }
        setIsSubmitting(false);
      });
  };

  const handleDismiss = async () => {
    if (!currentNotification) return;
    await handleActionClick("dismiss");
  };

  const getMainActions = (actions: NotificationAction[]) => {
    return actions.filter(
      (a) => a.style !== "secondary" && a.key !== "dismiss",
    );
  };

  if (!currentNotification) return null;

  return (
    <CustomModal
      open={openAlert}
      showCloseIcon={
        // Info/alert notifications are always dismissible; consent requests only
        // when they carry an explicit dismiss action (otherwise the modal would
        // trap the whole page with no way to close it).
        currentNotification?.type !== "consent_request" ||
        (currentNotification?.content?.actions?.some(
          (a) => a.key === "dismiss",
        ) ??
          false)
      }
      onClose={handleDismiss}
      header={
        <Typography variant="h3" fontWeight="bold">
          {currentNotification.content.title[currentLang]}
        </Typography>
      }
      body={
        <Box>
          <Box
            sx={{
              bgcolor: "grey.50",
              p: 2,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              maxHeight: "50dvh",
              overflowY: "auto",
              mb: 3,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                lineHeight: 1.7,
                whiteSpace: "pre-line",
                "& b, & strong": {
                  fontWeight: 600,
                  color: "text.primary",
                },
              }}
              dangerouslySetInnerHTML={{
                __html: currentNotification.content.message[currentLang],
              }}
            />
          </Box>

          {currentNotification.content.fullDocumentUrl && (
            <Button
              href={currentNotification.content.fullDocumentUrl}
              target="_blank"
              sx={{
                mb: 2,
                p: 0,
                textTransform: "none",
                textDecoration: "underline",
              }}
            >
              {t("Read-Full-Document")}
            </Button>
          )}

          {currentNotification.type === "consent_request" && (
            <Stack
              direction={{ xs: "column-reverse", sm: "row" }}
              spacing={{ xs: 2, sm: 3 }}
              justifyContent="flex-end"
              alignItems={{ xs: "stretch", sm: "center" }}
              sx={{ mt: 3, width: "100%" }}
            >
              {sortActions(
                getMainActions(currentNotification.content.actions),
              ).map((action) => {
                const { color, variant } = getButtonProps(action.style);

                return (
                  <Button
                    key={action.key}
                    variant={variant}
                    color={color}
                    onClick={() => handleActionClick(action.key)}
                    disabled={isSubmitting}
                    sx={{
                      width: { xs: "100%", sm: "auto" },
                      minWidth: { sm: 120 },
                    }}
                  >
                    {action.label[currentLang]}
                  </Button>
                );
              })}
            </Stack>
          )}

          {/* Info/alert notifications carry no consent actions — give them an
              explicit Close so the modal is never a dead-end. */}
          {currentNotification.type !== "consent_request" && (
            <Stack
              direction="row"
              justifyContent="flex-end"
              sx={{ mt: 3, width: "100%" }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handleDismiss}
                disabled={isSubmitting}
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { sm: 120 },
                }}
              >
                {t("Close")}
              </Button>
            </Stack>
          )}
        </Box>
      }
    />
  );
}
